import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight, Phone, Mail } from "lucide-react";
import { SITE, NAV, FOOTER_COLS } from "@/data/site";
import { trackEvent } from "@/lib/analytics";

export const Breadcrumbs = ({ items }) => {
  if (!items || items.length < 2) return null;
  return (
    <nav aria-label="Breadcrumb" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-2" data-testid="breadcrumbs">
      <ol className="flex flex-wrap items-center gap-1 text-xs font-mono uppercase tracking-wider text-dim">
        {items.map((c, i) => (
          <li key={c.href} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="w-3 h-3" aria-hidden="true" />}
            {i === items.length - 1 ? (
              <span className="text-mist" aria-current="page">{c.label}</span>
            ) : (
              <Link to={c.href} className="hover:text-amber transition-colors" data-testid={`breadcrumb-${i}`}>{c.label}</Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-ink/85 backdrop-blur-xl border-b border-line" data-testid="nav-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-baseline gap-2 shrink-0" data-testid="nav-logo" aria-label="Extreme Teambuilding — úvod">
          <span className="font-display font-800 font-extrabold uppercase tracking-tight text-lg text-white leading-none">Extreme Teambuilding</span>
          <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-[0.2em] text-dim">by Warehouse Liberec</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-6" aria-label="Hlavní navigace">
          {NAV.map((n) => (
            <Link key={n.href} to={n.href} className="text-sm font-semibold text-mist hover:text-white transition-colors uppercase tracking-wide" data-testid={`nav-${n.href.slice(1)}`}>
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/poptavka"
            onClick={() => trackEvent("click_primary_cta", { location: "header" })}
            className="hidden sm:inline-flex items-center bg-amber hover:bg-amberdark text-white font-display font-bold uppercase tracking-wide text-sm px-5 py-2 transition-colors"
            data-testid="header-cta"
          >
            Poptat misi
          </Link>
          <button className="lg:hidden text-white p-2" onClick={() => setOpen(!open)} aria-label={open ? "Zavřít menu" : "Otevřít menu"} data-testid="mobile-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="lg:hidden border-t border-line bg-ink px-4 py-4 flex flex-col gap-1" aria-label="Mobilní navigace" data-testid="mobile-menu">
          {NAV.map((n) => (
            <Link key={n.href} to={n.href} className="py-2 text-sm font-semibold text-mist hover:text-white uppercase tracking-wide" data-testid={`mobile-nav-${n.href.slice(1)}`}>
              {n.label}
            </Link>
          ))}
          <Link to="/poptavka" className="mt-2 bg-amber text-white font-display font-bold uppercase text-center py-3" data-testid="mobile-menu-cta">
            Poptat misi
          </Link>
        </nav>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="bg-tactical bg-[#050607] border-t border-line mt-24" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
        <div className="col-span-2 md:col-span-1">
          <p className="font-display font-extrabold uppercase text-white text-lg leading-tight">Extreme<br />Teambuilding</p>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-dim mt-1">by Warehouse Liberec</p>
          <p className="text-xs text-dim mt-4 leading-relaxed">
            {SITE.company}<br />
            {SITE.address}<br />
            IČO: {SITE.ico} · DIČ: {SITE.dic}
          </p>
          <a
            href={`mailto:${SITE.email}`}
            onClick={() => trackEvent("click_email", { location: "footer" })}
            className="inline-flex items-center gap-2 text-xs text-mist hover:text-amber mt-3 transition-colors"
            data-testid="footer-email"
          >
            <Mail className="w-3.5 h-3.5" /> {SITE.email}
          </a>
          <a
            href={`tel:${SITE.phone.replace(/\s/g, "")}`}
            onClick={() => trackEvent("click_phone", { location: "footer" })}
            className="flex items-center gap-2 text-xs text-mist hover:text-amber mt-2 transition-colors"
            data-testid="footer-phone"
          >
            <Phone className="w-3.5 h-3.5" /> {SITE.phone}
          </a>
        </div>
        {FOOTER_COLS.map((col) => (
          <nav key={col.heading} aria-label={col.heading}>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-dim mb-3">{col.heading}</p>
            <ul className="space-y-2">
              {col.links.map((l) => (
                <li key={l.href}>
                  <Link to={l.href} className="text-sm text-mist hover:text-white transition-colors" data-testid={`footer-link-${l.href.slice(1).replace(/\//g, "-")}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ))}
      </div>
      <div className="border-t border-line mt-10 pt-6 flex flex-col sm:flex-row justify-between gap-2">
        <p className="text-xs text-dim">© {new Date().getFullYear()} {SITE.company}. Všechna práva vyhrazena.</p>
        <Link to="/admin/login" className="text-xs text-dim hover:text-mist transition-colors" data-testid="footer-admin-link">Administrace</Link>
      </div>
    </div>
  </footer>
);

const StickyMobileCta = () => {
  const location = useLocation();
  if (["/poptavka", "/dekujeme"].includes(location.pathname) || location.pathname.startsWith("/admin")) return null;
  return (
    <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-ink/95 backdrop-blur border-t border-line p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]" data-testid="sticky-mobile-cta">
      <Link
        to="/poptavka"
        onClick={() => trackEvent("click_primary_cta", { location: "sticky_mobile" })}
        className="block w-full bg-amber hover:bg-amberdark text-white text-center font-display font-bold uppercase tracking-wide py-3 transition-colors"
        data-testid="sticky-mobile-cta-button"
      >
        Poptat teambuilding
      </Link>
    </div>
  );
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    try {
      if (!localStorage.getItem("et_cookie_consent")) setVisible(true);
    } catch { /* prerender */ }
  }, []);
  if (!visible) return null;
  const decide = (val) => {
    try { localStorage.setItem("et_cookie_consent", val); } catch { /* noop */ }
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("consent", "update", { analytics_storage: val === "accepted" ? "granted" : "denied" });
    }
    setVisible(false);
  };
  return (
    <div className="fixed bottom-16 md:bottom-4 inset-x-3 sm:inset-x-auto sm:right-4 sm:max-w-sm z-50 bg-elevated border border-linestrong p-4 shadow-2xl" role="dialog" aria-label="Souhlas s cookies" data-testid="cookie-banner">
      <p className="text-xs text-mist leading-relaxed">
        Používáme cookies k fungování webu a — s vaším souhlasem — k měření návštěvnosti a konverzí z kampaní. Detaily najdete na stránce <Link to="/cookies" className="underline hover:text-white">Cookies</Link>.
      </p>
      <div className="flex gap-2 mt-3">
        <button onClick={() => decide("accepted")} className="flex-1 bg-amber hover:bg-amberdark text-white text-xs font-bold uppercase tracking-wide py-2 transition-colors" data-testid="cookie-accept-button">
          Přijmout vše
        </button>
        <button onClick={() => decide("rejected")} className="flex-1 border border-linestrong text-mist hover:text-white text-xs font-bold uppercase tracking-wide py-2 transition-colors" data-testid="cookie-reject-button">
          Jen nezbytné
        </button>
      </div>
    </div>
  );
};

export const Layout = ({ children, breadcrumbs, hideFooterCta }) => (
  <div className="min-h-screen bg-ink flex flex-col">
    <Header />
    <main className="flex-1 pb-16 md:pb-0">
      <Breadcrumbs items={breadcrumbs} />
      {children}
    </main>
    <Footer />
    <StickyMobileCta />
    <CookieBanner />
  </div>
);
